// Same maths as the backend (utils/gst.ts) so forms can show totals before saving.
// All amounts are paise. Bills are always within Maharashtra, so tax is split into CGST + SGST.

export interface PriceInput {
  qty: number | string;
  /** Paise */
  rate?: number | null;
  /** Paise off the line */
  discount?: number | null;
  gstRate?: number | string | null;
}

export interface Totals {
  subTotal: number;
  cgst: number;
  sgst: number;
  totalTax: number;
  roundOff: number;
  grandTotal: number;
}

export function calcLine({ qty, rate, discount, gstRate }: PriceInput, isGst: boolean) {
  const gross = Math.round((Number(qty) || 0) * (rate || 0));
  const disc = Math.min(Math.round(discount || 0), gross);
  const taxableValue = gross - disc;
  const tax = isGst ? Math.round((taxableValue * (Number(gstRate) || 0)) / 100) : 0;
  const cgst = Math.floor(tax / 2);
  return { taxableValue, cgst, sgst: tax - cgst, tax, amount: taxableValue + tax };
}

export function computeTotals(lines: PriceInput[], isGst: boolean): Totals {
  const priced = lines.map((l) => calcLine(l, isGst));
  const sum = (k: 'taxableValue' | 'cgst' | 'sgst') => priced.reduce((a, l) => a + l[k], 0);
  const subTotal = sum('taxableValue');
  const cgst = sum('cgst');
  const sgst = sum('sgst');
  const raw = subTotal + cgst + sgst;
  const grandTotal = Math.round(raw / 100) * 100;
  return { subTotal, cgst, sgst, totalTax: cgst + sgst, roundOff: grandTotal - raw, grandTotal };
}

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function twoDigits(n: number): string {
  return n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? `-${ONES[n % 10]}` : ''}`;
}
function threeDigits(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  return [h ? `${ONES[h]} hundred` : '', r ? twoDigits(r) : ''].filter(Boolean).join(' ');
}

/** 265500 paise -> "Rupees two thousand six hundred fifty-five only" (Indian lakh/crore grouping). */
export function rupeesInWords(paise?: number | null): string {
  let n = Math.floor(Math.abs(paise || 0) / 100);
  if (!n) return 'Rupees zero only';
  const parts: string[] = [];
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) parts.push(`${threeDigits(crore)} crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} thousand`);
  if (n) parts.push(threeDigits(n));
  return `Rupees ${parts.join(' ')} only`;
}
